<!--
  Accesso Funzionari — single-page agents list (redesigned 2026-06-04).
  Replaces the legacy list + the embedded `<app-funzionari-dettagli>`
  modal (absorbed here) and keeps `<app-agente-list>` as the orders
  drawer (deferred restyle).

  Desktop only. Visual language coherent with /accounts.
-->

<div class="agents-page">
  <!-- ─── Sticky header ────────────────────────────────────────── -->
  <header class="agents-header">
    <div class="agents-header__titles">
      <h1 class="agents-header__title">👥 Agenti</h1>
      <p class="agents-header__subtitle">
        @if (agents.length > 0) {
          <strong>{{ filteredAgents.length }}</strong> di
          {{ agents.length }} agenti
        } @else if (loading) {
          Caricamento…
        } @else {
          Nessun agente registrato
        }
      </p>
    </div>

    <div class="agents-header__controls">
      <div class="search-wrap">
        <input
          type="text"
          class="search-input"
          placeholder="Cerca per nome, email, telefono…"
          [(ngModel)]="searchText"
          (ngModelChange)="onSearchInput()"
          aria-label="Cerca agente"
        />
        @if (searchText) {
          <button
            type="button"
            class="search-clear"
            (click)="clearSearch()"
            aria-label="Cancella ricerca"
            title="Cancella ricerca"
          >
            ×
          </button>
        }
      </div>
    </div>
  </header>

  <!-- ─── Table card ────────────────────────────────────────────── -->
  <section class="agents-table-card" aria-label="Elenco agenti">
    @if (loading && !hasAgents) {
      <!-- Skeleton during the first load -->
      <div class="table-skeleton" aria-busy="true">
        @for (i of [1, 2, 3, 4, 5, 6, 7]; track i) {
          <div class="table-skeleton__row">
            <div class="table-skeleton__avatar"></div>
            <div class="table-skeleton__cells">
              <div class="table-skeleton__line table-skeleton__line--lg"></div>
              <div class="table-skeleton__line table-skeleton__line--sm"></div>
            </div>
          </div>
        }
      </div>
    } @else if (filteredAgents.length === 0) {
      <div class="agents-empty">
        <div class="agents-empty__icon" aria-hidden="true">
          @if (!hasAgents) {
            🧑‍💼
          } @else {
            🔎
          }
        </div>
        <p class="agents-empty__msg">
          @if (!hasAgents) {
            Nessun agente registrato.
          } @else if (searchText) {
            Nessun agente corrisponde a "<strong>{{ searchText }}</strong
            >".
          } @else {
            Nessun agente.
          }
        </p>
      </div>
    } @else {
      <div class="prep-table-scroll">
        <table class="agents-table" role="table">
          <thead>
            <tr>
              <th class="col-avatar" aria-label="Avatar"></th>
              <th
                class="col-customer is-sortable"
                (click)="sortBy('ag_last_name')"
              >
                Nome
                @if (sortColumn === "ag_last_name") {
                  <span class="sort-indicator">{{
                    sortDirection === "asc" ? "▲" : "▼"
                  }}</span>
                }
              </th>
              <th class="col-customer is-sortable" (click)="sortBy('ag_email')">
                Email
                @if (sortColumn === "ag_email") {
                  <span class="sort-indicator">{{
                    sortDirection === "asc" ? "▲" : "▼"
                  }}</span>
                }
              </th>
              <th
                class="col-customer is-sortable"
                (click)="sortBy('ag_mobile')"
              >
                Telefono
                @if (sortColumn === "ag_mobile") {
                  <span class="sort-indicator">{{
                    sortDirection === "asc" ? "▲" : "▼"
                  }}</span>
                }
              </th>
              <th class="col-customer is-sortable" (click)="sortBy('ag_url')">
                Link
                @if (sortColumn === "ag_url") {
                  <span class="sort-indicator">{{
                    sortDirection === "asc" ? "▲" : "▼"
                  }}</span>
                }
              </th>
              <th class="col-actions" aria-label="Stato">Stato</th>
              <th class="col-actions" aria-label="Azioni"></th>
            </tr>
          </thead>
          <tbody>
            @for (agent of pagedAgents; track agent.agent_id) {
              <tr class="agents-row">
                <td class="col-avatar">
                  <div class="avatar">{{ initials(agent) }}</div>
                </td>
                <td class="col-name">
                  <span class="cell-name"
                    >{{ agent.ag_first_name }} {{ agent.ag_last_name }}</span
                  >
                </td>
                <td class="col-email">
                  <a
                    class="cell-email"
                    [attr.href]="'mailto:' + agent.ag_email"
                  >
                    {{ agent.ag_email }}
                  </a>
                </td>
                <td class="col-phone">
                  @if (agent.ag_mobile || agent.ag_phone) {
                    📞 {{ agent.ag_mobile || agent.ag_phone }}
                  } @else {
                    <span class="cell-muted">—</span>
                  }
                </td>
                <td class="col-link">
                  @if (agent.ag_url) {
                    <span
                      class="link-pill link-pill--ok"
                      title="Link personale generato"
                      >🔗 generato</span
                    >
                  } @else {
                    <span
                      class="link-pill link-pill--missing"
                      title="Nessun link generato"
                      >— mancante</span
                    >
                  }
                </td>
                <td class="col-status">
                  <span
                    class="status-pill"
                    [class.status-pill--active]="isActive(agent)"
                    [class.status-pill--inactive]="!isActive(agent)"
                  >
                    {{ isActive(agent) ? "Attivo" : "Disattivato" }}
                  </span>
                </td>
                <td class="col-actions">
                  <div class="row-actions">
                    <button
                      type="button"
                      class="icon-btn icon-btn--view"
                      title="Dettagli e link di accesso"
                      (click)="openView(agent)"
                    >
                      📋
                    </button>
                    <button
                      type="button"
                      class="icon-btn icon-btn--orders"
                      title="Visualizza ordini di questo agente"
                      (click)="openOrders(agent)"
                    >
                      📦
                    </button>
                    <button
                      type="button"
                      class="icon-btn icon-btn--edit"
                      title="Modifica dati agente"
                      (click)="openEdit(agent)"
                    >
                      ✏️
                    </button>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <!-- ─── Pagination ───────────────────────────────────────── -->
      @if (totalPages > 1) {
        <nav class="pagination" aria-label="Paginazione agenti">
          <button
            type="button"
            class="pagination__btn"
            [disabled]="currentPage === 0"
            (click)="prevPage()"
          >
            ‹ Precedente
          </button>
          <span class="pagination__info">
            Pagina <strong>{{ currentPage + 1 }}</strong> di {{ totalPages }} ·
            <span class="pagination__total"
              >{{ filteredAgents.length }} agenti</span
            >
          </span>
          <button
            type="button"
            class="pagination__btn"
            [disabled]="currentPage >= totalPages - 1"
            (click)="nextPage()"
          >
            Successiva ›
          </button>
        </nav>
      }
    }
  </section>

  <!-- ─── Modal layer (View + Edit) ────────────────────────────── -->
  @if (modalMode) {
    <div class="modal-backdrop" (click)="closeModal()">
      <div class="modal-card" (click)="$event.stopPropagation()">
        <!-- ── VIEW ─────────────────────────────────────────────── -->
        @if (modalMode === "view" && selectedAgent; as agent) {
          <header class="modal-header">
            <div class="modal-header__avatar">{{ initials(agent) }}</div>
            <div class="modal-header__text">
              <h2 class="modal-header__title">
                {{ agent.ag_first_name }} {{ agent.ag_last_name }}
              </h2>
              <p class="modal-header__subtitle">
                <span
                  class="status-pill"
                  [class.status-pill--active]="isActive(agent)"
                  [class.status-pill--inactive]="!isActive(agent)"
                  >{{ isActive(agent) ? "Attivo" : "Disattivato" }}</span
                >
              </p>
            </div>
            <button
              type="button"
              class="modal-close"
              (click)="closeModal()"
              aria-label="Chiudi"
            >
              ×
            </button>
          </header>

          <div class="modal-body modal-body--details">
            <dl class="details-grid">
              <div class="details-grid__row">
                <dt>Email</dt>
                <dd>
                  <a [attr.href]="'mailto:' + agent.ag_email">{{
                    agent.ag_email
                  }}</a>
                </dd>
              </div>
              <div class="details-grid__row">
                <dt>Telefono</dt>
                <dd>{{ agent.ag_phone || "—" }}</dd>
              </div>
              <div class="details-grid__row">
                <dt>Cellulare</dt>
                <dd>{{ agent.ag_mobile || "—" }}</dd>
              </div>
              @if (agent.ag_note) {
                <div class="details-grid__row details-grid__row--full">
                  <dt>Note</dt>
                  <dd class="details-grid__note">{{ agent.ag_note }}</dd>
                </div>
              }
            </dl>

            <!-- ── URL panel ────────────────────────────────────── -->
            <div class="url-panel" [class.url-panel--missing]="!selectedHasUrl">
              <div class="url-panel__header">
                <h3 class="url-panel__title">🔗 Link di accesso personale</h3>
                <p class="url-panel__hint">
                  @if (selectedHasUrl) {
                    L'agente può accedere ai suoi ordini con questo link.
                    Copialo, inviaglielo via email o rigeneralo se compromesso.
                  } @else {
                    Nessun link generato per questo agente. Genera ora per
                    consentirgli di accedere.
                  }
                </p>
              </div>

              @if (selectedHasUrl) {
                <div class="url-panel__value-row">
                  <code
                    class="url-panel__value"
                    [attr.title]="selectedFullUrl"
                    >{{ selectedFullUrl }}</code
                  >
                </div>
                <div class="url-panel__actions">
                  <button
                    type="button"
                    class="btn btn--ghost"
                    (click)="copyUrlToClipboard()"
                    [disabled]="copying"
                  >
                    @if (copying) {
                      Copia in corso…
                    } @else {
                      📋 Copia link
                    }
                  </button>
                  <button
                    type="button"
                    class="btn btn--ghost"
                    (click)="sendUrlByEmail()"
                  >
                    ✉️ Invia per email
                  </button>
                  <button
                    type="button"
                    class="btn btn--secondary"
                    (click)="generateUrl()"
                    [disabled]="generatingUrl"
                    title="Genera un nuovo link, sostituendo quello attuale"
                  >
                    @if (generatingUrl) {
                      <span
                        class="btn__spinner btn__spinner--dark"
                        aria-hidden="true"
                      ></span>
                      Rigenera…
                    } @else {
                      🔄 Rigenera
                    }
                  </button>
                  <button
                    type="button"
                    class="btn btn--danger-ghost"
                    (click)="deleteUrl()"
                    [disabled]="deletingUrl"
                  >
                    @if (deletingUrl) {
                      <span
                        class="btn__spinner btn__spinner--dark"
                        aria-hidden="true"
                      ></span>
                      Eliminazione…
                    } @else {
                      🗑️ Elimina link
                    }
                  </button>
                </div>
              } @else {
                <div class="url-panel__actions">
                  <button
                    type="button"
                    class="btn btn--primary"
                    (click)="generateUrl()"
                    [disabled]="generatingUrl"
                  >
                    @if (generatingUrl) {
                      <span class="btn__spinner" aria-hidden="true"></span>
                      Generazione…
                    } @else {
                      🔗 Genera link di accesso
                    }
                  </button>
                </div>
              }
            </div>
          </div>

          <footer class="modal-footer">
            <button type="button" class="btn btn--ghost" (click)="closeModal()">
              Chiudi
            </button>
            <button
              type="button"
              class="btn btn--primary"
              (click)="openEdit(agent)"
            >
              Modifica
            </button>
          </footer>
        }

        <!-- ── EDIT ─────────────────────────────────────────────── -->
        @if (modalMode === "edit" && selectedAgent) {
          <header class="modal-header">
            <h2 class="modal-header__title">Modifica agente</h2>
            <button
              type="button"
              class="modal-close"
              (click)="closeModal()"
              aria-label="Chiudi"
            >
              ×
            </button>
          </header>

          <form
            class="modal-body modal-body--form"
            [formGroup]="agentForm"
            (ngSubmit)="save()"
          >
            <div class="form-grid">
              <label class="form-field">
                <span class="form-field__label">Nome *</span>
                <input
                  class="form-field__input"
                  type="text"
                  formControlName="ag_first_name"
                />
                @if (
                  agentForm.controls["ag_first_name"].touched &&
                  agentForm.controls["ag_first_name"].invalid
                ) {
                  <span class="form-field__error">Il nome è obbligatorio.</span>
                }
              </label>

              <label class="form-field">
                <span class="form-field__label">Cognome *</span>
                <input
                  class="form-field__input"
                  type="text"
                  formControlName="ag_last_name"
                />
                @if (
                  agentForm.controls["ag_last_name"].touched &&
                  agentForm.controls["ag_last_name"].invalid
                ) {
                  <span class="form-field__error"
                    >Il cognome è obbligatorio.</span
                  >
                }
              </label>

              <label class="form-field form-field--full">
                <span class="form-field__label">Email *</span>
                <input
                  class="form-field__input"
                  type="email"
                  formControlName="ag_email"
                />
                @if (
                  agentForm.controls["ag_email"].touched &&
                  agentForm.controls["ag_email"].invalid
                ) {
                  <span class="form-field__error">
                    @if (agentForm.controls["ag_email"].errors?.["required"]) {
                      L'email è obbligatoria.
                    } @else {
                      Formato email non valido.
                    }
                  </span>
                }
              </label>

              <label class="form-field">
                <span class="form-field__label">Telefono</span>
                <input
                  class="form-field__input"
                  type="tel"
                  formControlName="ag_phone"
                />
              </label>

              <label class="form-field">
                <span class="form-field__label">Cellulare</span>
                <input
                  class="form-field__input"
                  type="tel"
                  formControlName="ag_mobile"
                />
              </label>

              <label class="form-field form-field--inline form-field--full">
                <input type="checkbox" formControlName="ag_active" />
                <span class="form-field__label">Agente attivo</span>
              </label>

              <label class="form-field form-field--full">
                <span class="form-field__label">Note</span>
                <textarea
                  class="form-field__input form-field__input--area"
                  rows="3"
                  formControlName="ag_note"
                ></textarea>
              </label>
            </div>
          </form>

          <footer class="modal-footer">
            <button
              type="button"
              class="btn btn--ghost"
              (click)="closeModal()"
              [disabled]="saving"
            >
              Annulla
            </button>
            <button
              type="button"
              class="btn btn--primary"
              (click)="save()"
              [disabled]="saving || agentForm.invalid"
            >
              @if (saving) {
                <span class="btn__spinner" aria-hidden="true"></span>
                Salvataggio…
              } @else {
                Salva
              }
            </button>
          </footer>
        }
      </div>
    </div>
  }

  <!-- ─── Orders drawer (mounts legacy <app-agente-list>) ─────── -->
  @if (showOrdersDrawer && selectedAgent) {
    <div class="orders-drawer-backdrop" (click)="closeOrders()">
      <aside class="orders-drawer" (click)="$event.stopPropagation()">
        <header class="orders-drawer__header">
          <div class="orders-drawer__title-block">
            <h2 class="orders-drawer__title">
              📦 Ordini di {{ selectedAgent.ag_first_name }}
              {{ selectedAgent.ag_last_name }}
            </h2>
          </div>
          <button
            type="button"
            class="modal-close"
            (click)="closeOrders()"
            aria-label="Chiudi"
          >
            ×
          </button>
        </header>
        <div class="orders-drawer__body">
          <app-agente-list
            [openOrders]="true"
            [agent_token]="selectedAgentToken"
          ></app-agente-list>
        </div>
      </aside>
    </div>
  }
</div>
